import { useCallback, useEffect, useMemo, useState } from 'react';
import { attendanceAPI } from '../services/api';
import { formatDate, getRoleDisplayName } from '../utils/helpers';
import { BriefcaseBusiness, Bus, CalendarDays, Download, RotateCcw, Search, UserRound, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import {
  getEmploymentTypeOptions,
  getEmploymentTypeLabel,
  getWorkArrangementLabel,
  workArrangementOptions,
} from '../utils/employment';

const statusColors = {
  present: 'bg-emerald-100 text-emerald-700',
  late: 'bg-amber-100 text-amber-700',
  half_day: 'bg-orange-100 text-orange-700',
  on_leave: 'bg-sky-100 text-sky-700',
  absent: 'bg-rose-100 text-rose-700',
};

const statusLabels = {
  all: 'All',
  present: 'Present',
  late: 'Late',
  half_day: 'Half Day',
  on_leave: 'On Leave',
  absent: 'Absent',
};

const SummaryCard = ({ label, value, tone }) => (
  <div className="app-surface rounded-[1.5rem] p-5">
    <p className="text-sm text-slate-500">{label}</p>
    <p className={`mt-2 text-3xl font-semibold ${tone}`}>{value ?? 0}</p>
  </div>
);

const Reports = () => {
  const { user, entityCategory, terminology } = useAuth();
  const employmentTypeOptions = useMemo(
    () => getEmploymentTypeOptions(user?.entity?.code),
    [user?.entity?.code]
  );
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [status, setStatus] = useState('all');
  const [sectionId, setSectionId] = useState('');
  const [busReaderId, setBusReaderId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [workArrangement, setWorkArrangement] = useState('');
  const [shiftId, setShiftId] = useState('');
  const [search, setSearch] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [hoursPeriod, setHoursPeriod] = useState('day');
  const [workingHours, setWorkingHours] = useState(null);
  const [hoursLoading, setHoursLoading] = useState(false);
  const [hoursRefresh, setHoursRefresh] = useState(0);
  const [overtimeRequests, setOvertimeRequests] = useState([]);
  const [overtimeForm, setOvertimeForm] = useState({
    user_id: '', overtime_date: new Date().toLocaleDateString('en-CA'),
    start_time: '', end_time: '', reason: '',
  });
  const [savingOvertime, setSavingOvertime] = useState(false);
  const canReviewOvertime = ['admin', 'manager'].includes(user?.role);

  const resetFilters = () => {
    setStatus('all');
    setSectionId('');
    setBusReaderId('');
    setSelectedUserId('');
    setEmploymentType('');
    setWorkArrangement('');
    setShiftId('');
    setSearch('');
  };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const response = await attendanceAPI.downloadDailyReport({
        date,
        status: status === 'all' ? undefined : status,
        section_id: sectionId || undefined,
        bus_reader_id: busReaderId || undefined,
        user_id: selectedUserId || undefined,
        employment_type: employmentType || undefined,
        work_arrangement: workArrangement || undefined,
        shift_id: shiftId || undefined,
        search: search.trim() || undefined,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `smartencampus-attendance-${date}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Attendance report downloaded');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to download attendance report');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    attendanceAPI.getDailyReport({
      date,
      status: status === 'all' ? undefined : status,
      section_id: sectionId || undefined,
      bus_reader_id: busReaderId || undefined,
      user_id: selectedUserId || undefined,
      employment_type: employmentType || undefined,
      work_arrangement: workArrangement || undefined,
      shift_id: shiftId || undefined,
    })
      .then((response) => setReport(response.data))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load attendance report'))
      .finally(() => setLoading(false));
  }, [date, status, sectionId, busReaderId, selectedUserId, employmentType, workArrangement, shiftId]);

  const loadOvertimeRequests = useCallback(() => {
    if (entityCategory !== 'office') return;
    attendanceAPI.getOvertimeRequests()
      .then((response) => setOvertimeRequests(response.data.requests || []))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load OT requests'));
  }, [entityCategory]);

  useEffect(() => {
    if (entityCategory !== 'office') return;
    setHoursLoading(true);
    attendanceAPI.getWorkingHours({
      date,
      period: hoursPeriod,
      user_id: selectedUserId || undefined,
    })
      .then((response) => setWorkingHours(response.data))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load working hours'))
      .finally(() => setHoursLoading(false));
  }, [date, entityCategory, hoursPeriod, selectedUserId, hoursRefresh]);

  useEffect(() => {
    loadOvertimeRequests();
  }, [entityCategory, loadOvertimeRequests]);

  const submitOvertime = async (event) => {
    event.preventDefault();
    setSavingOvertime(true);
    try {
      await attendanceAPI.createOvertimeRequest({
        ...overtimeForm,
        user_id: canReviewOvertime ? overtimeForm.user_id || undefined : undefined,
      });
      toast.success('OT request submitted for approval');
      setOvertimeForm((current) => ({ ...current, start_time: '', end_time: '', reason: '' }));
      loadOvertimeRequests();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit OT request');
    } finally {
      setSavingOvertime(false);
    }
  };

  const reviewOvertime = async (requestId, nextStatus) => {
    try {
      await attendanceAPI.reviewOvertimeRequest(requestId, { status: nextStatus });
      toast.success(`OT request ${nextStatus}`);
      loadOvertimeRequests();
      setHoursRefresh((current) => current + 1);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to review OT request');
    }
  };

  const filteredRecords = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (report?.records || []).filter((record) => {
      if (!needle) return true;
      const haystack = [
        record.full_name,
        record.employee_id,
        record.email,
        record.section_name,
        record.shift_name,
        record.bus_name,
        record.bus_location_name,
        record.location_name,
        record.role,
        record.employment_type,
        record.work_arrangement,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(needle);
    });
  }, [report, search]);

  const summary = report?.summary || {};
  const attendanceRate = summary.total
    ? Math.round((Number(summary.present_total || 0) / Number(summary.total || 1)) * 100)
    : 0;

  const cards = [
    { label: 'Total', value: summary.total, tone: 'text-slate-900' },
    { label: 'Present Today', value: summary.present_total, tone: 'text-emerald-700' },
    { label: 'Absent', value: summary.absent, tone: 'text-rose-700' },
    { label: 'On Leave', value: summary.on_leave, tone: 'text-sky-700' },
    { label: 'Late', value: summary.late, tone: 'text-amber-700' },
    { label: 'Attendance %', value: `${attendanceRate}%`, tone: 'text-teal-700' },
  ];
  const options = report?.filter_options || {};
  const sections = options.sections || report?.sections || [];
  const buses = options.buses || report?.buses || [];
  const users = options.users || report?.users || [];
  const shifts = options.shifts || report?.shifts || [];
  const availableUserIds = useMemo(
    () => new Set((report?.filter_options?.users || report?.users || []).map((option) => String(option.id))),
    [report]
  );
  const availableShiftIds = useMemo(
    () => new Set((report?.filter_options?.shifts || report?.shifts || []).map((option) => String(option.id))),
    [report]
  );
  const selectedDateIsToday = date === new Date().toLocaleDateString('en-CA');

  useEffect(() => {
    if (!loading && report && selectedUserId
      && !availableUserIds.has(String(selectedUserId))) {
      setSelectedUserId('');
    }
  }, [availableUserIds, loading, report, selectedUserId]);

  useEffect(() => {
    if (!loading && report && shiftId
      && !availableShiftIds.has(String(shiftId))) {
      setShiftId('');
    }
  }, [availableShiftIds, loading, report, shiftId]);
  const scopeLabel = {
    parent: 'Linked people only',
    teacher: 'Assigned teams only',
    student: 'Your attendance only',
    employee: 'Your attendance only',
    admin: 'All attendance',
    manager: 'All attendance',
  }[report?.scope] || 'Allowed attendance scope';

  return (
    <div className="space-y-6">
      <div className="app-hero rounded-[2rem] px-6 py-6 md:px-8">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="app-kicker">Attendance Reports</p>
            <h1 className="mt-2 text-3xl font-semibold">
              {selectedDateIsToday ? 'Today\'s attendance register' : 'Attendance register'}
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-white/75">
              View official attendance with names. Bus movement and raw scans are kept out of present counts.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            <label className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white/80">
              <span className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-white/60">
                <CalendarDays size={14} />
                Date
              </span>
              <input
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                className="w-full bg-transparent text-sm font-medium text-white outline-none"
              />
            </label>

            <label className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white/80">
              <span className="mb-1 block text-xs uppercase tracking-[0.24em] text-white/60">Attendance Policy</span>
              <select
                value={shiftId}
                onChange={(event) => setShiftId(event.target.value)}
                className="w-full bg-transparent text-sm font-medium text-white outline-none"
              >
                <option value="" className="text-slate-900">All attendance policies</option>
                {shifts.map((shift) => (
                  <option key={shift.id} value={shift.id} className="text-slate-900">
                    {shift.name}{shift.start_time && shift.end_time
                      ? ` (${String(shift.start_time).slice(0, 5)} - ${String(shift.end_time).slice(0, 5)})`
                      : ''}
                  </option>
                ))}
              </select>
            </label>

            <label className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white/80">
              <span className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-white/60">
                <Users size={14} />
                Status
              </span>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                className="w-full bg-transparent text-sm font-medium text-white outline-none"
              >
                {Object.entries(statusLabels).map(([value, label]) => (
                  <option key={value} value={value} className="text-slate-900">{label}</option>
                ))}
              </select>
            </label>

            <label className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white/80">
              <span className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-white/60">
                <BriefcaseBusiness size={14} />
                {terminology.group}
              </span>
              <select
                value={sectionId}
                onChange={(event) => {
                  setSectionId(event.target.value);
                  setSelectedUserId('');
                }}
                className="w-full bg-transparent text-sm font-medium text-white outline-none"
              >
                <option value="" className="text-slate-900">All teams</option>
                {sections.map((section) => (
                  <option key={section.id} value={section.id} className="text-slate-900">{section.name}</option>
                ))}
              </select>
            </label>

            <label className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white/80">
              <span className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-white/60">
                <Bus size={14} />
                Bus
              </span>
              <select
                value={busReaderId}
                onChange={(event) => setBusReaderId(event.target.value)}
                className="w-full bg-transparent text-sm font-medium text-white outline-none"
              >
                <option value="" className="text-slate-900">All buses</option>
                {buses.map((bus) => (
                  <option key={bus.id} value={bus.id} className="text-slate-900">
                    {bus.name || bus.reader_uid}
                  </option>
                ))}
              </select>
            </label>

            <label className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white/80 md:col-span-2 xl:col-span-1">
              <span className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-white/60">
                <UserRound size={14} />
                User
              </span>
              <select
                value={selectedUserId}
                onChange={(event) => setSelectedUserId(event.target.value)}
                className="w-full bg-transparent text-sm font-medium text-white outline-none"
              >
                <option value="" className="text-slate-900">All users</option>
                {users
                  .filter((user) => !sectionId || String(user.section_id) === String(sectionId))
                  .map((user) => (
                    <option key={user.id} value={user.id} className="text-slate-900">
                      {user.full_name}{user.employee_id ? ` (${user.employee_id})` : ''}
                    </option>
                  ))}
              </select>
            </label>

            <label className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white/80">
              <span className="mb-1 block text-xs uppercase tracking-[0.24em] text-white/60">Employment</span>
              <select
                value={employmentType}
                onChange={(event) => setEmploymentType(event.target.value)}
                className="w-full bg-transparent text-sm font-medium text-white outline-none"
              >
                <option value="" className="text-slate-900">All employment types</option>
                {employmentTypeOptions.map((option) => <option key={option.value} value={option.value} className="text-slate-900">{option.label}</option>)}
              </select>
            </label>

            <label className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white/80">
              <span className="mb-1 block text-xs uppercase tracking-[0.24em] text-white/60">Work Mode</span>
              <select
                value={workArrangement}
                onChange={(event) => setWorkArrangement(event.target.value)}
                className="w-full bg-transparent text-sm font-medium text-white outline-none"
              >
                <option value="" className="text-slate-900">All work modes</option>
                {workArrangementOptions.map((option) => <option key={option.value} value={option.value} className="text-slate-900">{option.label}</option>)}
              </select>
            </label>

            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex min-h-[74px] items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
            >
              <RotateCcw size={16} />
              Reset filters
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
        {cards.map((card) => (
          <SummaryCard key={card.label} {...card} />
        ))}
      </div>

      <div className="app-surface rounded-[1.75rem] p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
              {formatDate(`${date}T00:00:00`)}
            </p>
            <h2 className="mt-2 text-xl font-semibold text-slate-900">Attendance names</h2>
            <p className="mt-1 text-sm text-slate-500">
              Showing {filteredRecords.length} of {report?.records?.length || 0} people. {scopeLabel}.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <label className="flex min-w-[280px] items-center gap-3 rounded-2xl border border-slate-200 bg-white/70 px-4 py-3 text-sm text-slate-600">
              <Search size={16} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search name, ID, team, route, location..."
                className="w-full bg-transparent outline-none"
              />
            </label>
            <button
              type="button"
              onClick={downloadReport}
              disabled={loading || downloading}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-teal-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Download size={17} />
              {downloading ? 'Downloading...' : 'Download Report'}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400">Loading attendance report...</div>
        ) : filteredRecords.length === 0 ? (
          <div className="py-16 text-center text-slate-400">No attendance records found for this filter.</div>
        ) : (
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200/70 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-3 text-left">Name</th>
                  <th className="px-3 py-3 text-left">ID</th>
                  <th className="px-3 py-3 text-left">Role</th>
                  <th className="px-3 py-3 text-left">Employment</th>
                  <th className="px-3 py-3 text-left">Work Mode</th>
                  <th className="px-3 py-3 text-left">{terminology.group}</th>
                  <th className="px-3 py-3 text-left">Attendance Policy</th>
                  <th className="px-3 py-3 text-left">Scheduled</th>
                  <th className="px-3 py-3 text-left">Route</th>
                  <th className="px-3 py-3 text-left">Status</th>
                  <th className="px-3 py-3 text-left">IN Time</th>
                    <th className="px-3 py-3 text-left">OUT Time</th>
                    <th className="px-3 py-3 text-left">Early Timing</th>
                    <th className="px-3 py-3 text-left">Location</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((record) => (
                  <tr key={record.user_id} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-3 py-4">
                      <div className="font-medium text-slate-900">{record.full_name}</div>
                      <div className="text-xs text-slate-500">{record.email || '—'}</div>
                    </td>
                    <td className="px-3 py-4 font-mono text-xs text-slate-500">{record.employee_id || '—'}</td>
                    <td className="px-3 py-4 capitalize text-slate-600">{getRoleDisplayName(record.role, entityCategory)}</td>
                    <td className="px-3 py-4 text-slate-600">{getEmploymentTypeLabel(record.employment_type)}</td>
                    <td className="px-3 py-4 text-slate-600">{getWorkArrangementLabel(record.work_arrangement)}</td>
                    <td className="px-3 py-4 text-slate-600">{record.section_name || '—'}</td>
                    <td className="px-3 py-4 text-slate-600">
                      <div>{record.shift_name || '—'}</div>
                      <div className="text-xs capitalize text-slate-400">
                        {record.shift_assignment_source?.replace(/_/g, ' ') || ''}
                      </div>
                    </td>
                    <td className="px-3 py-4 text-slate-600">
                      {record.shift_start_time && record.shift_end_time
                        ? `${String(record.shift_start_time).slice(0, 5)} - ${String(record.shift_end_time).slice(0, 5)}`
                        : '—'}
                    </td>
                    <td className="px-3 py-4 text-slate-600">
                      <div>{record.bus_name || '—'}</div>
                      <div className="text-xs text-slate-400">
                        {record.bus_location_name || record.bus_status?.replace(/_/g, ' ') || ''}
                      </div>
                    </td>
                    <td className="px-3 py-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusColors[record.attendance_status] || 'bg-slate-100 text-slate-600'}`}>
                        {record.attendance_status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-3 py-4 text-slate-600">
                      {record.check_in_time_local?.slice(11, 16) || 'No scan'}
                    </td>
                    <td className="px-3 py-4 text-slate-600">
                      {record.check_out_time_local?.slice(11, 16) || 'Not marked'}
                    </td>
                    <td className="px-3 py-4 text-slate-600">
                      {record.is_early_login && <div className="text-sky-700">Login {record.early_login_minutes} min early</div>}
                      {record.is_early_logout && <div className="text-rose-700">Logout {record.early_logout_minutes} min early</div>}
                      {!record.is_early_login && !record.is_early_logout && '—'}
                    </td>
                    <td className="px-3 py-4 text-slate-600">{record.location_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="app-surface rounded-[1.75rem] p-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Persistent scan history</p>
          <h2 className="mt-2 text-xl font-semibold text-slate-900">Attendance event details</h2>
          <p className="mt-1 text-sm text-slate-500">
            {(report?.events || []).length} IN/OUT events recorded for the selected date and report scope.
          </p>
        </div>

        {(report?.events || []).length === 0 ? (
          <div className="py-12 text-center text-slate-400">No scan events found for this report.</div>
        ) : (
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200/70 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-3 text-left">Time</th>
                  <th className="px-3 py-3 text-left">Name</th>
                  <th className="px-3 py-3 text-left">ID</th>
                  <th className="px-3 py-3 text-left">{terminology.group}</th>
                  <th className="px-3 py-3 text-left">{terminology.area}</th>
                  <th className="px-3 py-3 text-left">Direction</th>
                  <th className="px-3 py-3 text-left">Status</th>
                  <th className="px-3 py-3 text-left">Reader</th>
                  <th className="px-3 py-3 text-left">Checkpoint</th>
                  <th className="px-3 py-3 text-left">Location</th>
                  <th className="px-3 py-3 text-left">Tag UID</th>
                </tr>
              </thead>
              <tbody>
                {(report?.events || []).map((event) => (
                  <tr key={event.id} className="border-b border-slate-100 last:border-b-0">
                    <td className="whitespace-nowrap px-3 py-4 text-slate-600">{event.scan_time_local || formatDate(event.scan_time)}</td>
                    <td className="px-3 py-4 font-medium text-slate-900">{event.full_name}</td>
                    <td className="px-3 py-4 font-mono text-xs text-slate-500">{event.employee_id || '—'}</td>
                    <td className="px-3 py-4 text-slate-600">{event.section_name || '—'}</td>
                    <td className="px-3 py-4 text-slate-600">{event.subject_name || '—'}</td>
                    <td className="px-3 py-4 font-medium uppercase text-slate-700">{event.direction || '—'}</td>
                    <td className="px-3 py-4 capitalize text-slate-600">{event.status?.replace(/_/g, ' ') || '—'}</td>
                    <td className="px-3 py-4 text-slate-600">{event.reader_name || event.reader_uid || '—'}</td>
                    <td className="px-3 py-4 capitalize text-slate-600">{event.checkpoint_type?.replace(/_/g, ' ') || '—'}</td>
                    <td className="px-3 py-4 text-slate-600">{event.location_name || '—'}</td>
                    <td className="px-3 py-4 font-mono text-xs text-slate-500">{event.tag_uid || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {entityCategory === 'office' && (
        <div className="app-surface rounded-[1.75rem] p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Office productivity</p>
              <h2 className="mt-2 text-xl font-semibold text-slate-900">Individual working hours</h2>
              <p className="mt-1 text-sm text-slate-500">
                Verified IN/OUT sessions compared with each person&apos;s assigned work schedule. Lunch and configured breaks are excluded.
              </p>
            </div>
            <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
              {['day', 'week', 'month'].map((period) => (
                <button
                  key={period}
                  type="button"
                  onClick={() => setHoursPeriod(period)}
                  className={`rounded-lg px-4 py-2 text-sm font-medium capitalize ${hoursPeriod === period ? 'bg-teal-700 text-white' : 'text-slate-600'}`}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>

          {hoursLoading ? (
            <div className="py-12 text-center text-slate-400">Calculating working hours...</div>
          ) : (workingHours?.rows || []).length === 0 ? (
            <div className="py-12 text-center text-slate-400">No scheduled people found for this period.</div>
          ) : (
            <div className="mt-6 overflow-x-auto">
              <div className="mb-4 text-sm text-slate-500">
                {workingHours.from} to {workingHours.to}
              </div>
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200/70 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-3 text-left">Person</th>
                    <th className="px-3 py-3 text-left">Team</th>
                    <th className="px-3 py-3 text-left">Schedule</th>
                    <th className="px-3 py-3 text-right">Scheduled</th>
                    <th className="px-3 py-3 text-right">Approved Leave</th>
                    <th className="px-3 py-3 text-right">Expected</th>
                    <th className="px-3 py-3 text-right">Worked</th>
                    <th className="px-3 py-3 text-right">Approved OT</th>
                    <th className="px-3 py-3 text-right">Unapproved Extra</th>
                    <th className="px-3 py-3 text-right">Deficit</th>
                    <th className="px-3 py-3 text-right">Incomplete</th>
                    <th className="px-3 py-3 text-right">Early Login</th>
                    <th className="px-3 py-3 text-right">Early Logout</th>
                  </tr>
                </thead>
                <tbody>
                  {(workingHours.rows || []).map((row) => (
                    <tr key={row.user_id} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-3 py-4">
                        <div className="font-medium text-slate-900">{row.full_name}</div>
                        <div className="font-mono text-xs text-slate-500">{row.employee_id || '—'}</div>
                      </td>
                      <td className="px-3 py-4 text-slate-600">{row.team_name || '—'}</td>
                      <td className="px-3 py-4 text-slate-600">{row.policy_name || '—'}</td>
                      <td className="px-3 py-4 text-right text-slate-600">{row.scheduled_hours.toFixed(2)} h</td>
                      <td className="px-3 py-4 text-right text-sky-700">{row.approved_leave_hours.toFixed(2)} h</td>
                      <td className="px-3 py-4 text-right text-slate-600">{row.expected_work_hours.toFixed(2)} h</td>
                      <td className="px-3 py-4 text-right font-semibold text-slate-900">{row.worked_hours.toFixed(2)} h</td>
                      <td className="px-3 py-4 text-right text-emerald-700">{row.approved_overtime_hours.toFixed(2)} h</td>
                      <td className="px-3 py-4 text-right text-amber-700">{row.unapproved_extra_hours.toFixed(2)} h</td>
                      <td className="px-3 py-4 text-right text-rose-700">{row.deficit_hours.toFixed(2)} h</td>
                      <td className="px-3 py-4 text-right text-slate-600">{row.incomplete_sessions}</td>
                      <td className="px-3 py-4 text-right text-sky-700">{row.early_login_count} ({row.early_login_hours.toFixed(2)} h)</td>
                      <td className="px-3 py-4 text-right text-rose-700">{row.early_logout_count} ({row.early_logout_hours.toFixed(2)} h)</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {entityCategory === 'office' && (
        <div className="app-surface rounded-[1.75rem] p-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Approval workflow</p>
            <h2 className="mt-2 text-xl font-semibold text-slate-900">Overtime requests</h2>
            <p className="mt-1 text-sm text-slate-500">Only approved time that overlaps actual attendance is counted as payable OT.</p>
          </div>
          <form onSubmit={submitOvertime} className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
            {canReviewOvertime && (
              <select required value={overtimeForm.user_id} onChange={(event) => setOvertimeForm({ ...overtimeForm, user_id: event.target.value })} className="rounded-xl border border-slate-300 px-3 py-2 text-sm">
                <option value="">Select person</option>
                {users.map((option) => <option key={option.id} value={option.id}>{option.full_name}</option>)}
              </select>
            )}
            <input required type="date" value={overtimeForm.overtime_date} onChange={(event) => setOvertimeForm({ ...overtimeForm, overtime_date: event.target.value })} className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
            <input required type="time" value={overtimeForm.start_time} onChange={(event) => setOvertimeForm({ ...overtimeForm, start_time: event.target.value })} className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
            <input required type="time" value={overtimeForm.end_time} onChange={(event) => setOvertimeForm({ ...overtimeForm, end_time: event.target.value })} className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
            <input required minLength={3} maxLength={1000} value={overtimeForm.reason} onChange={(event) => setOvertimeForm({ ...overtimeForm, reason: event.target.value })} placeholder="Reason for OT" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
            <button disabled={savingOvertime} className="rounded-xl bg-teal-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {savingOvertime ? 'Submitting...' : 'Request OT'}
            </button>
          </form>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200/70 text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="px-3 py-3 text-left">Person</th><th className="px-3 py-3 text-left">Date</th><th className="px-3 py-3 text-left">Window</th><th className="px-3 py-3 text-left">Reason</th><th className="px-3 py-3 text-left">Status</th><th className="px-3 py-3 text-left">Action</th></tr>
              </thead>
              <tbody>
                {overtimeRequests.map((request) => (
                  <tr key={request.id} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-3 py-4 font-medium text-slate-900">{request.full_name}</td>
                    <td className="px-3 py-4 text-slate-600">{String(request.overtime_date).slice(0, 10)}</td>
                    <td className="px-3 py-4 text-slate-600">{String(request.start_time).slice(0, 5)}–{String(request.end_time).slice(0, 5)}</td>
                    <td className="px-3 py-4 text-slate-600">{request.reason}</td>
                    <td className="px-3 py-4 capitalize text-slate-600">{request.status}</td>
                    <td className="px-3 py-4">
                      {canReviewOvertime && request.status === 'pending' ? (
                        <div className="flex gap-2">
                          <button type="button" onClick={() => reviewOvertime(request.id, 'approved')} className="rounded-lg bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700">Approve</button>
                          <button type="button" onClick={() => reviewOvertime(request.id, 'rejected')} className="rounded-lg bg-rose-100 px-3 py-1.5 text-xs font-semibold text-rose-700">Reject</button>
                        </div>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
